import { useStatistics } from '../../context';

import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import './style.scss';
import { type Filter } from '../..';
import { RootState, useAppSelector } from 'careand-redux/store';
import { IsContinuityOfCareEnabled } from 'careand-redux/selectors';
import {
  Appointment,
  AppointmentConcern,
  ClinicStatisticsNew,
  PhysicianPhysicianTypeEnum,
  Shift,
  StatisticsEvent,
  Subscription,
} from 'generated/api';
import { Appointments, Subscriptions } from 'components/Clinic/ClinicStats';
import CalendarUtils from 'services/CalendarUtils';
import moment from 'moment-timezone';
import AppointmentUtils, { APPOINTMENT_STATUS } from '../../../../../services/AppointmentUtils';
import { Bar } from 'react-chartjs-2';
import { CalendarMonthToolbar } from '../../../../../components/Schedule/TimeZoneAgnosticBigCalendar';
import PerformanceUtils from '../../../../../services/PerformanceUtils';
import InlineToggle, { InlineSwitch } from '../../components/InlineToggle';
import PhysicianUtils from '../../../../../services/PhysicianUtils';
import ATable from '../../../../../components/Clinic/ClinicFinancials/ATable';
import Info from '../../components/info';
import DashboardBox, { ALLOW_ALL } from '../../components/DashboardBox';
import PatientsWithMostAppointments from '../../components/PatientsWithMostAppointments';
import {
  CONVERSION_WINDOW,
  getConversionsByMonth,
  getNewHealthConcernIds,
  isPPUConversionAppointment,
  weightedRate,
} from 'services/PPUConversionUtils';

const StaffSection = ({ filter }: { filter: Filter }) => {
  const { data } = useStatistics();
  const physicians = useAppSelector((state) => state.physicians.byId);
  return (
    <ClinicSection
      physicianId={filter.type === 'practitioner' ? filter.id : undefined}
      clinicId={filter.type === 'clinic' ? filter.id : undefined}
      data={data}
      physicians={physicians}
      filter={filter}
      clinicChanges={data?.clinicChanges ?? []}
    />
  );
};

function ClinicSection({
  physicianId,
  clinicId,
  data,
  physicians,
  filter,
  clinicChanges,
}: {
  physicianId?: string;
  clinicId?: string;
  data?: ClinicStatisticsNew;
  physicians: RootState['physicians']['byId'];
  filter: Filter;
  clinicChanges: StatisticsEvent[];
}) {
  const showNPs = useState(true);
  const scheduledTimeHandler = useState(true);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const continuityEnabled = useAppSelector(IsContinuityOfCareEnabled);

  const patientSortHandler = useState(0);
  const patientSortSections = useMemo(
    () => [APPOINTMENT_STATUS.completed, APPOINTMENT_STATUS.no_show, APPOINTMENT_STATUS.cancelled],
    [],
  );

  const clinicsToShowRoster = useMemo(() => {
    if (clinicId) return [clinicId];
    if (filter.type === 'practitioner') {
      const cid = physicians[filter.id!]?.clinic_id;
      return cid ? [cid] : undefined;
    }
    return clinics.map((it) => it.id!);
  }, [filter, physicians, clinicId, clinics]);

  return (
    <div className="clinic-practitioner-section">
      <div className="grid gap-[var(--inner-gap)]">
        <DashboardBox
          title="Rostered Subscribers"
          info="Number of subscribers per month"
          allow={ALLOW_ALL}
          hidden={!continuityEnabled}
        >
          <Subscriptions
            key={JSON.stringify(filter)}
            data={data!}
            physicians={physicianId ? { [physicianId]: physicians[physicianId]! } : {}}
            allPhysicians={physicians}
            showClinics={clinicsToShowRoster}
            showTitle={false}
            showLegend={true}
          />
        </DashboardBox>
        <DashboardBox
          title="Canceled Appointments"
          info="Number of canceled appointments"
          allow={ALLOW_ALL}
        >
          <CanceledAppointmentsChart
            key={clinicId || physicianId}
            physicianId={physicianId}
            clinicId={clinicId}
            appointments={data?.appointments}
          />
        </DashboardBox>

        <div className="auto-two-cols">
          <DashboardBox
            title="No-Show Appointments"
            info="Number of no-show appointments"
            allow={ALLOW_ALL}
          >
            <NoShowAppointmentsChart
              key={clinicId || physicianId}
              physicianId={physicianId}
              clinicId={clinicId}
              appointments={data?.appointments}
            />
          </DashboardBox>
          <DashboardBox
            title="Appointment Duration"
            info="Number of appointments per scheduled appointment duration"
            allow={ALLOW_ALL}
          >
            <AppointmentTimeChart
              key={clinicId || physicianId}
              physicianId={physicianId}
              clinicId={clinicId}
              appointments={data?.appointments}
            />
          </DashboardBox>
        </div>
        <div className="auto-two-cols">
          <DashboardBox allow={ALLOW_ALL}>
            {data && (
              <ConsultationTimeCharts
                key={clinicId || physicianId}
                physicianId={physicianId}
                clinicId={clinicId}
                appointments={data?.appointments}
              />
            )}
          </DashboardBox>
        </div>
        <div className="auto-two-cols">
          <DashboardBox
            title="Appointment Wait Time"
            info="How long patients take to be called in, from the scheduled time"
            allow={ALLOW_ALL}
          >
            <WaitTimeChart
              key={clinicId || physicianId}
              physicianId={physicianId}
              clinicId={clinicId}
              appointments={data?.appointments}
            />
          </DashboardBox>
          <DashboardBox
            title="Schedule Utilization Rate"
            titleExtras={
              <>
                <InlineToggle
                  titleOn="Scheduled Time"
                  titleOff="Patient-facing Time"
                  handler={scheduledTimeHandler}
                />
                {!physicianId && <InlineToggle titleOn="NPs" titleOff="RPNs" handler={showNPs} />}
              </>
            }
            info="Portion of the time that the practitioner's schedule is taken by scheduled appointments, admin time, or nothing. 'Scheduled Time' includes no-shows and the full appointment time. 'Patient-facing Time' only counts time spent with patients or on a call."
            allow={ALLOW_ALL}
          >
            <UtilizationRateChart
              key={clinicId || physicianId}
              physicianId={physicianId}
              clinicId={clinicId}
              appointments={data?.appointments}
              shifts={data?.shifts}
              scheduledTime={scheduledTimeHandler[0]}
              showNPs={showNPs[0]}
            />
          </DashboardBox>
        </div>
        <div className="auto-two-cols">
          <DashboardBox
            title="PPU Conversion Rates"
            info="How many Pay-per-Use patients seen by an NP for a New Health Concern end up subscribing"
            allow={ALLOW_ALL}
          >
            <ConversionRateChart
              key={clinicId || physicianId}
              physicianId={physicianId}
              clinicId={clinicId}
              clinicChanges={clinicChanges}
              appointments={data?.appointments}
              subscriptions={data?.subscriptions}
            />
          </DashboardBox>
        </div>
        <DashboardBox
          title="Top 100 patients with most appointments"
          info="Top 100 patients with most appointments in the last 365 days (excluding lab appointments)"
          allow={ALLOW_ALL}
          titleExtras={
            <span>
              <InlineSwitch titles={patientSortSections} handler={patientSortHandler} />
            </span>
          }
        >
          <PatientsWithMostAppointments
            data={data!}
            clinicId={clinicId}
            physicianId={physicianId}
            sortBy={patientSortSections[patientSortHandler[0]]}
          />
        </DashboardBox>
      </div>
    </div>
  );
}

function ConversionRateChart({
  clinicId,
  physicianId,
  appointments,
  subscriptions,
  clinicChanges,
}: {
  clinicId?: string;
  physicianId?: string;
  appointments?: Appointment[];
  subscriptions?: Subscription[];
  clinicChanges: StatisticsEvent[];
}) {
  const physicians = useAppSelector((state) => state.physicians.byId);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const options = useAppSelector((state) => state.appointments.options);
  const clinicName = clinicId ? clinics.find((c) => c.id === clinicId)?.name : '';

  const allClinics = !clinicId && !physicianId;

  const newConcernIds = useMemo(() => getNewHealthConcernIds(options), [options]);

  const completedPPU = useMemo(() => {
    if (!appointments) return [];
    return _.sortBy(
      appointments.filter((appointment) =>
        filterByClinicOrPhysician(appointment, clinicId, physicianId, undefined, (appointment) =>
          isPPUConversionAppointment(appointment, newConcernIds, physicians, clinicId),
        ),
      ),
      'date',
    );
  }, [appointments, filterByClinicOrPhysician, clinicId, physicianId, physicians, newConcernIds]);

  const { datasets, types, tableData } = useMemo(() => {
    let ppuById: Record<string, Appointment[]> = {};

    completedPPU.forEach((appt) => {
      let { physician_id, clinic_id } = appt;
      if (allClinics) {
        ppuById[clinic_id!] = ppuById[clinic_id!] || [];
        ppuById[clinic_id!]!.push(appt);
      } else if (physician_id) {
        ppuById[physician_id] = ppuById[physician_id] || [];
        ppuById[physician_id]!.push(appt);
      }
    });

    const FORMAT = 'MMYYYY';
    let datasets = Object.keys(ppuById).map((id) => {
      let byMonth = getConversionsByMonth(ppuById[id]!, subscriptions, FORMAT);

      let result = [];
      let date = moment.unix(completedPPU[0]!.date!);
      let endDate = moment().subtract(CONVERSION_WINDOW, 'seconds');
      while (date.isBefore(endDate)) {
        let month = date.format(FORMAT);
        let appointments = byMonth[month]?.appointments.size || 0;
        let conversions = byMonth[month]?.conversions.size || 0;
        let rate = appointments > 0 ? (conversions / appointments) * 100.0 : null;

        let info = undefined;
        if (allClinics) {
          info = clinics.find((c) => c.id === id)?.name;
        } else if (physicianId) {
          info = PhysicianUtils.getName(physicians[physicianId]);
        } else {
          info = PhysicianUtils.getName(physicians[id]);
        }

        result.push({
          date: date.unix(),
          info,
          value: rate,
          appointments,
          conversions,
        });
        date.add(1, 'month');
      }

      return { result, id };
    });

    let types = undefined;
    if (allClinics) {
      types = clinics.map(({ name }) => name);
    } else if (physicianId) {
      types = [PhysicianUtils.getName(physicians[physicianId])];
    } else {
      types = Object.keys(ppuById)
        .filter(
          (id) =>
            !physicians[id]!.physician_type ||
            physicians[id]!.physician_type === PhysicianPhysicianTypeEnum.NursePractitioner,
        )
        .map((id) => PhysicianUtils.getName(physicians[id]));
    }

    // Calculate averages
    const now = moment().subtract(CONVERSION_WINDOW, 'seconds');

    const periods = {
      '3m': now.clone().subtract(3, 'months'),
      '6m': now.clone().subtract(6, 'months'),
      '12m': now.clone().subtract(12, 'months'),
      ytd: moment().startOf('year').isBefore(now) ? moment().startOf('year') : now.clone(),
      all: moment(0), // beginning of time
    };

    const calculatePeriodData = (
      data: (typeof datasets)[number]['result'],
      startDate: moment.Moment,
    ) => {
      let relevantData = data.filter((item) => moment.unix(item.date).isAfter(startDate));
      return {
        rate: weightedRate(relevantData),
        appointments: _.sumBy(relevantData, 'appointments'),
        conversions: _.sumBy(relevantData, 'conversions'),
      };
    };

    let averages = datasets.map(({ result, id }) => {
      let periodData = Object.entries(periods).reduce(
        (acc, [key, startDate]) => {
          acc[key] = calculatePeriodData(result, startDate);
          return acc;
        },
        {} as Record<string, { rate: number | null; appointments: number; conversions: number }>,
      );

      return { id, periodData };
    });

    // Sort averages by all-time rate
    averages.sort((a, b) => {
      const rateA = a.periodData['all']!.rate;
      const rateB = b.periodData['all']!.rate;
      if (rateA === null && rateB === null) return 0;
      if (rateA === null) return 1;
      if (rateB === null) return -1;
      return rateB - rateA; // Sort in descending order
    });

    // Prepare table data
    const headers = [
      'Name',
      'Last 3 Months',
      'Last 6 Months',
      'Last 12 Months',
      'Year to Date',
      'All Time',
    ];

    const rows = averages.map(({ id, periodData }) => [
      PhysicianUtils.getName(physicians[id]) || clinics.find((c) => c.id === id)?.name || '',
      ...['3m', '6m', '12m', 'ytd', 'all'].map((period) => {
        const { rate, appointments, conversions } = periodData[period]!;
        return rate !== null ? `${rate.toFixed(2)}% (${conversions}/${appointments})` : 'N/A';
      }),
    ]);

    return {
      datasets: _.flatten(datasets.map((d) => d.result)),
      types,
      tableData: { headers, rows },
    };
  }, [completedPPU, subscriptions, physicians, clinicName, allClinics]);

  return (
    <div className="auto-two-cols">
      <Appointments
        appointments={datasets}
        types={_.compact(types)}
        average={true}
        chartOptions={{
          stacked: false,
          override: {
            scales: {
              y: {
                max: 100,
                title: {
                  display: true,
                  text: 'Conversion Rate (%)',
                  font: {
                    size: 14,
                    weight: 'bold',
                  },
                },
              },
            },
          },
        }}
      />
      <ATable headers={tableData.headers} rows={tableData.rows} />
    </div>
  );
}

const UTILIZATION_TYPES = {
  admin: 'Admin Time',
  appointments: 'Patient-facing Time',
  empty: 'Other',
} as const;

function UtilizationRateChart({
  clinicId,
  physicianId,
  appointments,
  shifts,
  showNPs,
  scheduledTime,
}: {
  clinicId?: string;
  physicianId?: string;
  appointments?: Appointment[];
  shifts?: Shift[];
  showNPs: boolean;
  scheduledTime: boolean;
}) {
  const physicians = useAppSelector((state) => state.physicians.byId);

  const showOnlyNPs = !physicianId && showNPs;
  const showOnlyRPNs = !physicianId && !showNPs;

  const isNP = useCallback(
    (id: string) => {
      const type = physicians[id]?.physician_type;
      return !type || type === PhysicianPhysicianTypeEnum.NursePractitioner;
    },
    [physicians],
  );

  const isRPN = useCallback(
    (id: string) => physicians[id]?.physician_type === PhysicianPhysicianTypeEnum.Nurse,
    [physicians],
  );

  const filterByClinicOrPhysician = useCallback(
    <T extends Appointment | Shift>(
      item: T,
      clinicId?: string,
      physicianId?: string,
      extraFilter?: (item: T) => boolean,
    ) => {
      let clinicFilter = clinicId ? clinicId === item.clinic_id : true;
      let physicianFilter = physicianId
        ? physicianId === ((item as Appointment).physician_id || (item as Shift).user_id)
        : true;
      let typeFilter =
        (!showOnlyNPs && !showOnlyRPNs) ||
        (showOnlyNPs && isNP((item as Appointment).physician_id || (item as Shift).user_id!)) ||
        (showOnlyRPNs && isRPN((item as Appointment).physician_id || (item as Shift).user_id!));
      return clinicFilter && physicianFilter && typeFilter && (!extraFilter || extraFilter(item));
    },
    [clinicId, physicianId, showOnlyNPs, showOnlyRPNs, isNP, isRPN],
  );

  const filteredAppointments = useMemo(() => {
    if (!appointments) return [];
    return _.sortBy(
      appointments.filter((appointment) =>
        filterByClinicOrPhysician(
          appointment,
          clinicId,
          physicianId,
          (appointment) =>
            appointment.status !== AppointmentUtils.getAppointmentStatus().cancelled &&
            (scheduledTime
              ? true
              : appointment.status !== AppointmentUtils.getAppointmentStatus().no_show),
        ),
      ),
      'date',
    );
  }, [appointments, filterByClinicOrPhysician, clinicId, physicianId, scheduledTime]);

  const filteredShifts = useMemo(() => {
    if (!shifts) return [];
    return _.sortBy(
      shifts.filter((shift) => filterByClinicOrPhysician(shift, clinicId, physicianId)),
      'start',
    );
  }, [shifts, filterByClinicOrPhysician, clinicId, physicianId]);

  const data = useMemo(() => {
    const byDate = new Map();

    filteredShifts.forEach((shift) => {
      const date = CalendarUtils.format(shift.start, 'DDMMYYYY');
      if (!byDate.has(date)) {
        byDate.set(date, {
          total: 0,
          [UTILIZATION_TYPES.admin]: 0,
          [UTILIZATION_TYPES.appointments]: 0,
          [UTILIZATION_TYPES.empty]: 0,
        });
      }
      const dateData = byDate.get(date);

      dateData.total += shift.end! - shift.start!;
      shift.breaks?.forEach((b) => {
        const start = CalendarUtils.militaryTimeToUnix(shift.start, b.start_hour!);
        const end = CalendarUtils.militaryTimeToUnix(shift.start, b.end_hour!);
        const breakSeconds = end - start;
        if (b.name === 'Lunch') {
          dateData.total -= breakSeconds;
        } else {
          dateData[UTILIZATION_TYPES.admin] += breakSeconds;
        }
      });
    });

    filteredAppointments.forEach((appointment) => {
      const date = CalendarUtils.format(appointment.date!, 'DDMMYYYY');
      if (byDate.has(date)) {
        const dateData = byDate.get(date);
        let scheduledDuration = appointment.date_end! - appointment.date!;
        let duration = scheduledTime
          ? scheduledDuration
          : Math.min(
              AppointmentUtils.calculateDuration(appointment, 'seconds')!,
              scheduledDuration,
            );
        dateData[UTILIZATION_TYPES.appointments] += duration;
      }
    });

    byDate.forEach((dateData, date) => {
      const total = dateData.total;
      dateData[UTILIZATION_TYPES.admin] /= total;
      dateData[UTILIZATION_TYPES.appointments] /= total;
      dateData[UTILIZATION_TYPES.empty] =
        1 - dateData[UTILIZATION_TYPES.admin] - dateData[UTILIZATION_TYPES.appointments];
    });

    return _.sortBy(
      Array.from(byDate).flatMap(([date, dateData]) =>
        Object.values(UTILIZATION_TYPES).map((type) => ({
          date: moment(date, 'DDMMYYYY').unix(),
          info: type,
          value: dateData[type] * 100,
        })),
      ),
      'date',
    );
  }, [filteredAppointments, filteredShifts]);

  return (
    <div className="grid grid-cols-1 gap-[var(--inner-gap)]">
      <Appointments
        appointments={data}
        increment={false}
        average={true}
        types={Object.values(UTILIZATION_TYPES)}
        chartOptions={{
          override: {
            scales: {
              y: {
                max: 100,
                title: {
                  display: true,
                  text: 'Utilization Rate  (%)',
                  font: {
                    size: 14,
                    weight: 'bold',
                  },
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

function WaitTimeChart({
  clinicId,
  physicianId,
  appointments,
}: {
  clinicId?: string;
  physicianId?: string;
  appointments?: Appointment[];
}) {
  const completedAppointments = useMemo(() => {
    if (appointments) {
      return _.sortBy(
        appointments
          .filter((appointment) =>
            filterByClinicOrPhysician(
              appointment,
              clinicId,
              physicianId,
              AppointmentUtils.getAppointmentStatus().completed,
              (appointment) => !!appointment.date_called_in && !!appointment.date,
            ),
          )
          .map((appointment) => {
            let waitTime = Math.max(0, (appointment.date_called_in! - appointment.date!) / 60);
            return {
              ...appointment,
              info: 'Wait Time',
              value: waitTime,
            };
          }),
        'date',
      );
    }
    return [];
  }, [clinicId, physicianId, appointments]);

  return (
    <div className="grid grid-cols-1 gap-[var(--inner-gap)]">
      <Appointments
        appointments={completedAppointments}
        increment={false}
        average={true}
        types={['Wait Time']}
        chartOptions={{
          override: {
            scales: {
              y: {
                max: 6,
                title: {
                  display: true,
                  text: 'Average Wait Time (minutes)',
                  font: {
                    size: 14,
                    weight: 'bold',
                  },
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

const NO_SHOW_TYPES = { noShow: 'No Show' };

function NoShowAppointmentsChart({
  clinicId,
  physicianId,
  appointments,
}: {
  clinicId?: string;
  physicianId?: string;
  appointments?: Appointment[];
}) {
  const noShowAppointments = useMemo(() => {
    if (appointments) {
      return _.sortBy(
        appointments
          .filter((appointment) =>
            filterByClinicOrPhysician(
              appointment,
              clinicId,
              physicianId,
              AppointmentUtils.getAppointmentStatus().no_show,
            ),
          )
          .map((appointment) => {
            return {
              ...appointment,
              info: 'No Show',
            };
          }),
        'date',
      );
    }
    return [];
  }, [clinicId, physicianId, appointments]);

  return (
    <div className="grid grid-cols-1 gap-[var(--inner-gap)]">
      <Appointments
        appointments={noShowAppointments}
        increment={true}
        types={Object.values(NO_SHOW_TYPES)}
        chartOptions={{
          override: {
            scales: {
              y: {
                title: {
                  display: true,
                  text: '# Appointments',
                  font: {
                    size: 14,
                    weight: 'bold',
                  },
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

const TYPES = { sameDay: 'Same Day', all: 'All' };
const CANCELED_KEY = 'date_canceled';
function CanceledAppointmentsChart({
  clinicId,
  physicianId,
  appointments,
}: {
  clinicId?: string;
  physicianId?: string;
  appointments?: Appointment[];
}) {
  const canceledAppointments = useMemo(() => {
    if (appointments) {
      return _.sortBy(
        appointments
          .filter((appointment) =>
            filterByClinicOrPhysician(
              appointment,
              clinicId,
              physicianId,
              AppointmentUtils.getAppointmentStatus().cancelled,
            ),
          )
          .map((appointment) => {
            let sameDay = CalendarUtils.isSameDay(
              moment.unix(appointment.date_canceled!),
              moment.unix(appointment.date!),
            );
            return {
              ...appointment,
              info: sameDay ? TYPES.sameDay : TYPES.all,
            };
          }),
        CANCELED_KEY,
      );
    }
    return [];
  }, [clinicId, physicianId, appointments]);

  const byPatient = useMemo(() => {
    return canceledAppointments.filter(
      (appointment) =>
        appointment.status_history?.find(({ status }) => status === 'Cancelled')?.by_role ===
        'patient',
    );
  }, [canceledAppointments]);

  const byStaff = useMemo(() => {
    return canceledAppointments.filter(
      (appointment) =>
        appointment.status_history?.find(({ status }) => status === 'Cancelled')?.by_role !==
        'patient',
    );
  }, [canceledAppointments]);

  const maxY = useMemo(() => {
    let max = 0;
    const format = 'MM/YYYY';
    let byDate: Record<string, number> = {};
    canceledAppointments.forEach((appointment) => {
      let date = appointment[CANCELED_KEY];
      let str = moment.unix(date!).format(format);
      byDate[str] = (byDate[str] || 0) + 1;
      max = Math.max(byDate[str]!, max);
    });
    return max + 1;
  }, [canceledAppointments]);

  return (
    <div className="auto-two-cols">
      <Appointments
        appointments={byPatient}
        increment={true}
        title="By Patient"
        types={Object.values(TYPES)}
        dateKey={CANCELED_KEY}
        chartOptions={{
          showTitle: false,
          override: {
            scales: {
              y: {
                max: maxY,
                title: {
                  display: true,
                  text: '# Appointments',
                  font: {
                    size: 14,
                    weight: 'bold',
                  },
                },
              },
            },
          },
        }}
      />
      <Appointments
        appointments={byStaff}
        increment={true}
        title="By Staff"
        types={Object.values(TYPES)}
        dateKey={CANCELED_KEY}
        chartOptions={{
          showTitle: false,
          override: {
            scales: {
              y: {
                max: maxY,
                title: {
                  display: true,
                  text: '# Appointments',
                  font: {
                    size: 14,
                    weight: 'bold',
                  },
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

function filterByClinicOrPhysician(
  appointment: Appointment,
  clinicId?: string,
  physicianId?: string,
  status?: string,
  customFilter?: (appointment: Appointment) => boolean,
) {
  let statusFilter = status ? appointment.status === status : true;
  let customFilterResult = customFilter ? customFilter(appointment) : true;
  let clinicFilter = clinicId ? appointment.clinic_id === clinicId : true;
  let physicianFilter = physicianId ? appointment.physician_id === physicianId : true;
  return statusFilter && customFilterResult && clinicFilter && physicianFilter;
}

function AppointmentTimeChart({
  clinicId,
  physicianId,
  appointments,
}: {
  clinicId?: string;
  physicianId?: string;
  appointments?: Appointment[];
}) {
  const options = useAppSelector((state) => state.appointments.options);

  const concernMap = useMemo(() => {
    const concerns = _.flattenDeep(options.map((option) => option.concerns ?? [])).filter(
      (concern): concern is AppointmentConcern => !!concern,
    );
    return PerformanceUtils.arrayToObject(concerns ?? []);
  }, [options]);

  const filteredAppointments = useMemo(() => {
    if (appointments) {
      return _.sortBy(
        appointments
          .filter((appointment) =>
            filterByClinicOrPhysician(
              appointment,
              clinicId,
              physicianId,
              AppointmentUtils.getAppointmentStatus().completed,
            ),
          )
          .map((appointment) => {
            let minutes = concernMap[appointment.concern_id!]?.minutes + '';
            return {
              ...appointment,
              info: minutes,
            };
          }),
        'date',
      );
    }
    return [];
  }, [clinicId, physicianId, appointments, concernMap]);

  const types = useMemo(() => {
    return _.uniq(Object.values(concernMap).map(({ minutes }) => minutes + '')).sort();
  }, [concernMap]);

  return (
    <div className="grid grid-cols-1 gap-[var(--inner-gap)]">
      <Appointments
        appointments={filteredAppointments}
        increment={true}
        types={types}
        chartOptions={{
          showTitle: false,
          override: {
            scales: {
              y: {
                title: {
                  display: true,
                  text: '# Appointments',
                  font: {
                    size: 14,
                    weight: 'bold',
                  },
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

const CONSULTATION_TIME_OPTIONS = {
  hours: 'Hours',
  appointments: '# Appointments',
};

function ConsultationTimeCharts({
  clinicId,
  physicianId,
  appointments,
}: {
  clinicId?: string;
  physicianId?: string;
  appointments?: Appointment[];
}) {
  const [date, setDate] = useState(moment().add(-1, 'month').startOf('month').unix());
  const optionHandler = useState(0);
  const [option] = optionHandler;

  const optionsArray = Object.values(CONSULTATION_TIME_OPTIONS);

  return (
    <>
      <span className="dashboard-section-title">
        Consultation Time
        <Info tooltip="Time spent for each appointment concern and scheduled appointment time" />
        <InlineSwitch titles={optionsArray} handler={optionHandler} />
      </span>
      <div className="grid grid-cols-1 gap-[var(--inner-gap)] mb-2 mx-auto">
        <CalendarMonthToolbar
          date={date}
          setDate={setDate}
          chevronHeight="20px"
          style={{ margin: 0, alignItems: 'center' }}
        />
      </div>
      <div className="auto-two-cols">
        <ConsultationTimeChart
          clinicId={clinicId}
          physicianId={physicianId}
          appointments={appointments}
          concernKey="text"
          date={date}
          groupBy={optionsArray[option]!}
        />
        <ConsultationTimeChart
          clinicId={clinicId}
          physicianId={physicianId}
          appointments={appointments}
          concernKey="minutes"
          date={date}
          groupBy={optionsArray[option]!}
        />
      </div>
    </>
  );
}

function ConsultationTimeChart({
  clinicId,
  physicianId,
  appointments,
  concernKey,
  date,
  groupBy,
}: {
  clinicId?: string;
  physicianId?: string;
  appointments?: Appointment[];
  concernKey?: keyof AppointmentConcern;
  date: number;
  groupBy: string;
}) {
  const FORMAT = 'MMM YYYY';
  const options = useAppSelector((state) => state.appointments.options);

  const formattedDate = moment.unix(date).format(FORMAT);

  const concernMap = useMemo(() => {
    const concerns = _.compact(_.flattenDeep(options.map((option) => option.concerns ?? [])));
    return PerformanceUtils.arrayToObject(concerns ?? []);
  }, [options]);

  const filteredAppointments = useMemo(() => {
    if (appointments) {
      return _.sortBy(
        appointments.filter((appointment) =>
          filterByClinicOrPhysician(
            appointment,
            clinicId,
            physicianId,
            AppointmentUtils.getAppointmentStatus().completed,
            (appointment) => moment.unix(appointment.date!).format(FORMAT) === formattedDate,
          ),
        ),
        'date',
      );
    }
    return [];
  }, [clinicId, physicianId, appointments, formattedDate]);

  const datasets = useMemo(() => {
    let duration: Record<string, { x: string; y: number }> = {};
    let consultation: Record<string, { x: string; y: number }> = {};

    filteredAppointments.forEach((appointment) => {
      const { concern_id } = appointment;
      let concernText = `${concernMap![concern_id!]?.[concernKey!]}`;
      let minutes = concernMap[concern_id!]?.minutes;
      if (concernText) {
        if (!duration[concernText]) {
          duration[concernText] = {
            x: concernText,
            y: 0,
          };
        }
        if (!consultation[concernText]) {
          consultation[concernText] = {
            x: concernText,
            y: 0,
          };
        }

        let durationValue = groupBy === CONSULTATION_TIME_OPTIONS.hours ? minutes! / 60 : 1;
        duration[concernText]!.y += durationValue;

        let appointmentDuration = AppointmentUtils.calculateDuration(appointment) || minutes;
        if (Number.isNaN(appointmentDuration)) appointmentDuration = minutes;
        let ratio = appointmentDuration! / minutes!;

        let consultationValue =
          groupBy === CONSULTATION_TIME_OPTIONS.hours
            ? Math.min(appointmentDuration!, minutes! * 1.2) / 60
            : Math.min(ratio, 1.2);
        consultation[concernText]!.y += consultationValue;
      }
    });

    return [
      {
        label: 'Appointment Duration',
        values: _.sortBy(Object.values(duration), 'y').reverse(),
      },
      {
        label: 'Consultation Time',
        values: _.sortBy(Object.values(consultation), 'y').reverse(),
      },
    ];
  }, [filteredAppointments, concernMap, concernKey, groupBy]);

  return (
    <div className="grid grid-cols-1 gap-[var(--inner-gap)]">
      <BarChart
        //@ts-expect-error
        datasets={datasets}
        stacked={false}
        displayLegend={true}
        override={{
          scales: {
            y: {
              title: {
                display: true,
                text: groupBy,
                font: {
                  size: 14,
                  weight: 'bold',
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

const BarChart = ({
  datasets = [],
  height = '350px',
  title = '',
  stacked = true,
  displayLegend = false,
  //@ts-expect-error
  override,
}) => {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: displayLegend,
      },
      colors: {
        enabled: true,
      },
      title: {
        display: !!title,
        text: title,
      },
    },
    scales: {
      x: {
        stacked,
      },
      y: {
        beginAtZero: true,
        stacked,
      },
    },
  };

  const data = {
    datasets: datasets.map((d) => {
      return {
        //@ts-expect-error
        label: d.label,
        //@ts-expect-error
        data: d.values,
      };
    }),
  };

  _.merge(options, override ?? {});

  return (
    <div className="chart" style={{ height }}>
      <Bar data={data} options={options} />
    </div>
  );
};

export default StaffSection;
